import { IMAGES } from "@/lib/constants";
import ScrollReveal from "@/components/ScrollReveal";
import { Heart, Eye, ShieldCheck, Users, Lightbulb, Building, Handshake, Network } from "lucide-react";

const values = [
  { icon: Heart, title: "Community First", desc: "Every initiative is designed with, by, and for the people it serves." },
  { icon: Eye, title: "Transparency & Accountability", desc: "Every rupee, every programme, every outcome is documented and open." },
  { icon: Building, title: "Sustainability Over Spectacle", desc: "We build hospitals, not camps. FPOs, not food drops. Institutions, not events." },
  { icon: Users, title: "Women-Led Transformation", desc: "Our leadership, SHGs, and frontline workers are overwhelmingly women — by design." },
  { icon: Lightbulb, title: "Youth as Nation-Builders", desc: "We invest in young people as architects of the Jharkhand they want to inherit." },
];

const modelSteps = [
  { icon: Handshake, title: "Embed, Don't Parachute", desc: "We live within the communities we serve." },
  { icon: Building, title: "Build Institutions, Not Campaigns", desc: "Schools, hospitals, FPOs — permanent infrastructure." },
  { icon: Network, title: "Integrate Every Vertical", desc: "One family, multiple touchpoints, compounding impact." },
  { icon: ShieldCheck, title: "Leverage Local Governance", desc: "Connecting citizens to district officials through Rakshak24." },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.arjunMunda})` }} />
        <div className="absolute inset-0 bg-[#1B4332]/65" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center pt-20">
          <ScrollReveal>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white font-bold mb-4">
              Rooted in Jharkhand.<br />Built for India.
            </h1>
            <p className="text-white/80 text-base sm:text-lg font-outfit max-w-xl mx-auto">
              Two decades of ground-up social transformation.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Our Story */}
      <section data-testid="our-story" className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">Our Journey</p>
            <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold mb-8">Our Story</h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <div className="space-y-6 text-[#333] font-outfit leading-relaxed">
              <p>
                MBT Welfare & Development Trust was founded on a simple conviction: true development
                begins at the community level. What started as a small group of dedicated individuals
                in the heart of Jharkhand has grown into a comprehensive social impact organisation
                serving thousands of families across multiple districts.
              </p>
              <p>
                Unlike organisations that parachute into communities for short-term projects, MBT is
                an embedded, community-led platform. Our team lives within the villages they serve,
                building relationships that span years and programmes that create permanent change.
                We don't run campaigns — we build institutions.
              </p>
              <p>
                Over two decades, we have established Farmer Producer Organisations supporting 5,000+
                farmers, launched women's self-help groups that have transformed 1,800+ households,
                built education programmes reaching tribal children in 50+ villages, and developed
                youth leadership networks that turn young people into changemakers.
              </p>
              <p>
                Our integrated model — where healthcare, education, agriculture, and empowerment
                reinforce each other — ensures that every family we serve benefits from multiple
                touchpoints, creating compounding impact that sustains itself long after our
                programmes begin.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section data-testid="mission-vision" className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">Purpose</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold">Mission & Vision</h2>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal delay={0.1}>
              <div data-testid="mission-card" className="bg-[#1B4332] rounded-xl p-8 md:p-10 text-white h-full">
                <h3 className="font-playfair text-xl sm:text-2xl font-semibold mb-4 text-[#D4A843]">Our Mission</h3>
                <p className="font-outfit leading-relaxed text-white/85">
                  To uplift the most underserved communities across Jharkhand through integrated
                  programmes in healthcare, education, agriculture, and empowerment — creating
                  self-sustaining ecosystems where every individual can thrive with dignity.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div data-testid="vision-card" className="bg-white rounded-xl p-8 md:p-10 border-2 border-[#D4A843] h-full">
                <h3 className="font-playfair text-xl sm:text-2xl font-semibold mb-4 text-[#1B4332]">Our Vision</h3>
                <p className="font-outfit leading-relaxed text-[#333]">
                  A Jharkhand where no village is forgotten, no farmer goes unsupported, no child
                  is denied education, and no woman is held back — a state that leads India in
                  inclusive, ground-up development by 2040.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section data-testid="core-values" className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">What We Stand For</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold">Core Values</h2>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {values.map((val, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="bg-white rounded-xl p-6 md:p-8 shadow-[0_4px_20px_rgba(27,67,50,0.06)] h-full">
                  <div className="w-12 h-12 rounded-lg bg-[#1B4332]/5 flex items-center justify-center mb-5">
                    <val.icon size={24} className="text-[#2D6A4F]" />
                  </div>
                  <h3 className="font-playfair text-lg text-[#1B4332] font-semibold mb-3">{val.title}</h3>
                  <p className="text-[#666] text-sm font-outfit leading-relaxed">{val.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* The MBT Model */}
      <section data-testid="mbt-model" className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12 md:mb-16">
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">How We Work</p>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold">The MBT Model</h2>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {modelSteps.map((step, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#1B4332] flex items-center justify-center mx-auto mb-5">
                    <step.icon size={28} className="text-[#D4A843]" />
                  </div>
                  <div className="text-[#D4A843] font-outfit font-bold text-sm mb-2">Step {i + 1}</div>
                  <h3 className="font-playfair text-lg text-[#1B4332] font-semibold mb-2">{step.title}</h3>
                  <p className="text-[#666] text-sm font-outfit leading-relaxed">{step.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section data-testid="leadership" className="bg-[#F0F7F4] py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <img src={IMAGES.arjunMunda} alt="Community Leadership" className="rounded-xl shadow-lg w-full h-80 object-cover" loading="lazy" />
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div>
                <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#D4A843] font-outfit mb-4">Leadership</p>
                <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl text-[#1B4332] font-bold mb-6">
                  Led by the Community, for the Community
                </h2>
                <p className="text-[#333] font-outfit leading-relaxed mb-4">
                  MBT's leadership brings over 20 years of dedicated social work across Jharkhand.
                  Our founder's hands-on presence in villages — not boardrooms — ensures that every
                  programme reflects ground realities and community aspirations.
                </p>
                <p className="text-[#333] font-outfit leading-relaxed">
                  Our advisory network includes government bodies, CSR partners, elected representatives,
                  and most importantly, the community leaders who guide our work every day. Women-led
                  grassroots leadership is not just a goal — it's our operating model.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </main>
  );
}
